import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, Loader2, Upload, X, Sparkles, Mail, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import { postSellLead, postTradeEstimate } from "@/lib/api";
import { openEmail, openSMS } from "@/lib/contact";

const empty = {
  make: "",
  model: "",
  year: "",
  mileage: "",
  condition: "Good",
  intent: "Either",
  name: "",
  email: "",
  phone: "",
  notes: "",
  send_copy: true,
};

export default function SellTrade() {
  const [f, setF] = useState(empty);
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [estLoading, setEstLoading] = useState(false);
  const [estimate, setEstimate] = useState(null);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));

  const onPhotos = (e) => {
    const files = Array.from(e.target.files || []).slice(0, 4);
    Promise.all(
      files.map(
        (file) =>
          new Promise((res) => {
            const r = new FileReader();
            r.onload = () => res(r.result);
            r.readAsDataURL(file);
          })
      )
    ).then((arr) => setPhotos((prev) => [...prev, ...arr].slice(0, 4)));
  };

  const getEstimate = async () => {
    if (!f.make || !f.model || !f.year || !f.mileage) {
      toast.error("Add make, model, year and mileage to get a ballpark");
      return;
    }
    setEstLoading(true);
    setEstimate(null);
    try {
      const res = await postTradeEstimate({
        make: f.make,
        model: f.model,
        year: f.year,
        mileage: f.mileage,
        condition: f.condition,
        notes: f.notes || null,
        photos_count: photos.length,
      });
      setEstimate(res);
    } catch (e) {
      toast.error("Couldn't fetch a ballpark. Submit the request and we'll appraise directly.");
    } finally {
      setEstLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!f.make || !f.model || !f.year || !f.name || !f.email || !f.phone) {
      toast.error("Please fill in required fields");
      return;
    }
    setLoading(true);
    try {
      await postSellLead({ ...f, photos });
      // Open the visitor's own mail app pre-filled to your inbox -
      // instant delivery without an API key.
      openEmail({
        subject: `Sell / Trade · ${f.year} ${f.make} ${f.model}`,
        cc: f.send_copy ? f.email : undefined,
        body: [
          "New Sell / Trade request from strivemotorsfl.com",
          "",
          `Name:      ${f.name}`,
          `Email:     ${f.email}`,
          `Phone:     ${f.phone}`,
          "",
          `Vehicle:   ${f.year} ${f.make} ${f.model}`,
          `Mileage:   ${f.mileage}`,
          `Condition: ${f.condition}`,
          `Intent:    ${f.intent}`,
          `Photos:    ${photos.length ? `${photos.length} attached via web form` : "none"}`,
          "",
          `Notes:     ${f.notes || "-"}`,
        ].join("\n"),
      });
      toast.success("Offer request saved · a pre-filled email opened - tap Send in your mail app.");
      setF(empty);
      setPhotos([]);
      setEstimate(null);
    } catch {
      toast.error("Something went wrong. Please call us at 561-502-6769.");
    } finally {
      setLoading(false);
    }
  };

  const fmt = (n) => `$${Math.round(n).toLocaleString()}`;

  return (
    <section id="sell" data-testid="sell-section" className="relative py-28 lg:py-40 px-6 lg:px-10 bg-[#0A0A0A] border-t border-[#1c1c1c] overflow-hidden">
      <div className="absolute -left-40 top-1/3 w-[600px] h-[600px] spotlight-bl pointer-events-none" />
      <div className="max-w-[1400px] mx-auto relative">
        <div className="flex items-baseline gap-4 mb-14">
          <span className="font-mono2 text-[11px] uppercase tracking-[0.3em] text-[#E31E24]">
            (03) · Sell or Trade
          </span>
          <span className="flex-1 h-px bg-white/15 mt-1" />
        </div>

        <div className="grid grid-cols-12 gap-10 lg:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9 }}
            className="col-span-12 lg:col-span-6"
          >
            <h2 className="font-anton uppercase leading-[0.9] text-[10vw] md:text-[7vw] lg:text-[5vw] mb-8">
              Get a <span className="font-cormorant italic font-medium text-[#E31E24]">real offer</span> for your car
            </h2>
            <p className="text-white/70 max-w-lg text-[15px] lg:text-lg leading-relaxed mb-10">
              Snap a few photos, tell us the basics, and get an <span className="text-white">instant ballpark</span>
              &nbsp;before we call. Cash in hand or trade credit · you choose.
            </p>

            <ol className="space-y-6 border-l border-[#E31E24] pl-6 max-w-md mb-10">
              {[
                "Tell us about your vehicle + upload photos",
                "See an instant AI ballpark range",
                "We follow up with your real, human offer",
              ].map((t, i) => (
                <li key={i} className="flex items-baseline gap-4">
                  <span className="font-cormorant italic text-[#E31E24] text-2xl leading-none">
                    0{i + 1}.
                  </span>
                  <span className="text-white/85">{t}</span>
                </li>
              ))}
            </ol>

            {/* Direct contact rail · no email exposed */}
            <div className="border-t border-white/15 pt-6">
              <div className="font-mono2 text-[11px] uppercase tracking-[0.28em] text-white/60 mb-3">
                Prefer to send it yourself?
              </div>
              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  data-testid="direct-email-btn"
                  onClick={() =>
                    openEmail({
                      subject: `Sell / Trade · ${f.year || ""} ${f.make || ""} ${f.model || ""}`.trim(),
                      body: [
                        "Hi Strive Motors,",
                        "",
                        "I'd like a quote on my vehicle:",
                        `Year:      ${f.year || "(add year)"}`,
                        `Make:      ${f.make || "(add make)"}`,
                        `Model:     ${f.model || "(add model)"}`,
                        `Mileage:   ${f.mileage || "(add mileage)"}`,
                        `Condition: ${f.condition}`,
                        "",
                        "Notes:",
                        f.notes || "",
                        "",
                        "Thanks!",
                      ].join("\n"),
                    })
                  }
                  className="inline-flex items-center gap-2 border border-white/40 hover:border-[#E31E24] hover:text-[#E31E24] px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em] transition-colors"
                >
                  <Mail size={14} /> Email Us Direct
                </button>
                <button
                  type="button"
                  data-testid="direct-sms-btn"
                  onClick={() =>
                    openSMS({
                      body: `Hi Strive · interested in selling/trading my ${f.year || ""} ${f.make || ""} ${f.model || ""}`.trim(),
                    })
                  }
                  className="inline-flex items-center gap-2 border border-white/40 hover:border-[#E31E24] hover:text-[#E31E24] px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em] transition-colors"
                >
                  <MessageSquare size={14} /> Text Us
                </button>
              </div>
              <div className="mt-3 text-white/50 text-[11px] font-mono2">
                Opens your mail / messages app · nothing gets shared publicly.
              </div>
            </div>
          </motion.div>

          <motion.form
            onSubmit={submit}
            data-testid="sell-form"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.1 }}
            className="col-span-12 lg:col-span-6 grid grid-cols-2 gap-4 border border-[#E31E24]/30 p-6 lg:p-10 bg-[#141414]/50 backdrop-blur-md"
          >
            {[
              ["make", "Make *"],
              ["model", "Model *"],
              ["year", "Year *"],
              ["mileage", "Mileage *"],
            ].map(([n, l]) => (
              <label key={n} className="flex flex-col gap-2 border-b border-white/15 focus-within:border-[#E31E24] pb-2 col-span-2 sm:col-span-1">
                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/70">{l}</span>
                <input
                  data-testid={`sell-input-${n}`}
                  value={f[n]}
                  onChange={(e) => set(n, e.target.value)}
                  className="bg-transparent outline-none text-[15px] py-1"
                />
              </label>
            ))}

            <label className="col-span-2 sm:col-span-1 flex flex-col gap-2 border-b border-white/15 pb-2">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/70">Condition</span>
              <select
                data-testid="sell-input-condition"
                value={f.condition}
                onChange={(e) => set("condition", e.target.value)}
                className="bg-transparent outline-none text-[15px] py-1 text-white"
              >
                {["Excellent", "Good", "Fair", "Rough"].map((c) => (
                  <option key={c} value={c} className="bg-[#141414]">{c}</option>
                ))}
              </select>
            </label>
            <label className="col-span-2 sm:col-span-1 flex flex-col gap-2 border-b border-white/15 pb-2">
              <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/70">Intent</span>
              <select
                data-testid="sell-input-intent"
                value={f.intent}
                onChange={(e) => set("intent", e.target.value)}
                className="bg-transparent outline-none text-[15px] py-1 text-white"
              >
                {["Cash", "Trade", "Either"].map((c) => (
                  <option key={c} value={c} className="bg-[#141414]">{c}</option>
                ))}
              </select>
            </label>

            {/* Photos */}
            <div className="col-span-2 mt-2">
              <label className="flex items-center gap-3 border border-dashed border-white/25 hover:border-[#E31E24] px-4 py-4 cursor-pointer transition-colors">
                <Upload size={18} className="text-white/70" />
                <span className="text-white/70 text-sm font-mono2 uppercase tracking-widest">
                  Snap or upload photos (up to 4)
                </span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={onPhotos} data-testid="sell-photos" />
              </label>
              {photos.length > 0 && (
                <div className="mt-3 flex flex-wrap gap-2">
                  {photos.map((p, i) => (
                    <div key={i} className="relative w-20 h-20">
                      <img src={p} alt={`Vehicle photo ${i + 1}`} className="w-full h-full object-cover" />
                      <button
                        type="button"
                        aria-label={`Remove photo ${i + 1}`}
                        onClick={() => setPhotos(photos.filter((_, ix) => ix !== i))}
                        className="absolute -top-2 -right-2 bg-[#E31E24] text-white w-6 h-6 flex items-center justify-center"
                      >
                        <X size={12} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Instant estimate CTA */}
            <div className="col-span-2 flex flex-wrap items-center gap-3">
              <button
                type="button"
                onClick={getEstimate}
                disabled={estLoading}
                data-testid="trade-estimate-btn"
                className="inline-flex items-center gap-2 border border-[#E31E24] text-[#E31E24] hover:bg-[#E31E24] hover:text-white px-4 py-2.5 font-mono2 uppercase text-[11px] tracking-[0.24em] transition-colors disabled:opacity-60"
              >
                {estLoading ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                Instant ballpark
              </button>
              <span className="text-white/50 text-xs font-mono2">
                AI estimate · Real offer follows human review
              </span>
            </div>

            <AnimatePresence>
              {estimate && (
                <motion.div
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  data-testid="trade-estimate-card"
                  className="col-span-2 border border-[#E31E24]/60 bg-[#E31E24]/8 p-6"
                >
                  <div className="font-mono2 text-[10px] uppercase tracking-[0.3em] text-[#E31E24] mb-3">
                    Instant Ballpark · {f.year} {f.make} {f.model}
                  </div>
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {[
                      { label: "Quick Cash", val: estimate.low },
                      { label: "Fair Trade", val: estimate.mid, hero: true },
                      { label: "Clean Private", val: estimate.high },
                    ].map((t, i) => (
                      <div key={i} className={`border ${t.hero ? "border-[#E31E24] bg-[#E31E24]/10" : "border-white/15"} p-4`}>
                        <div className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/60">
                          {t.label}
                        </div>
                        <div className={`font-anton uppercase mt-1 ${t.hero ? "text-white text-2xl lg:text-3xl" : "text-white/85 text-xl lg:text-2xl"}`}>
                          {fmt(t.val)}
                        </div>
                      </div>
                    ))}
                  </div>
                  <p className="text-white/75 text-[13px] leading-relaxed">
                    {estimate.message}{" "}
                    <span className="text-white">
                      Choose trade credit and add ~{estimate.trade_credit_boost_pct}% toward your next vehicle.
                    </span>
                  </p>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Contact fields */}
            {[
              ["name", "Name *", "col-span-2"],
              ["email", "Email *"],
              ["phone", "Phone *"],
              ["notes", "Notes", "col-span-2"],
            ].map(([n, l, extra]) => (
              <label key={n} className={`flex flex-col gap-2 border-b border-white/15 focus-within:border-[#E31E24] pb-2 ${extra || "col-span-2 sm:col-span-1"}`}>
                <span className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-white/70">{l}</span>
                <input
                  data-testid={`sell-input-${n}`}
                  type={n === "email" ? "email" : "text"}
                  value={f[n]}
                  onChange={(e) => set(n, e.target.value)}
                  className="bg-transparent outline-none text-[15px] py-1"
                />
              </label>
            ))}

            <div className="col-span-2 mt-2">
              <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={f.send_copy}
                  onChange={(e) => set("send_copy", e.target.checked)}
                  data-testid="sell-send-copy"
                  className="sr-only peer"
                />
                <span className="w-5 h-5 border border-white/40 peer-checked:bg-[#E31E24] peer-checked:border-[#E31E24] flex items-center justify-center transition-colors">
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" className={f.send_copy ? "opacity-100" : "opacity-0"}>
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                </span>
                <span className="text-white/80 text-sm">Send me a copy of this request</span>
              </label>
            </div>

            <div className="col-span-2 mt-4">
              <button
                type="submit"
                disabled={loading}
                data-testid="sell-submit"
                className="group w-full inline-flex items-center justify-between gap-6 bg-[#E31E24] hover:bg-white hover:text-[#0A0A0A] text-white px-8 py-5 transition-colors duration-300"
              >
                <span className="font-anton uppercase tracking-[0.14em] text-xl">
                  {loading ? "Sending..." : "Send my info · Get real offer"}
                </span>
                {loading ? <Loader2 size={22} className="animate-spin" /> : <ArrowUpRight size={22} className="group-hover:rotate-45 transition-transform" />}
              </button>
              <div className="mt-4 text-white/60 text-xs font-mono2 uppercase tracking-widest">
                A pre-filled email will open in your mail app · Tap Send to reach Strive instantly
              </div>
            </div>
          </motion.form>
        </div>
      </div>
    </section>
  );
}
